import { ChangeDetectionStrategy, Component, EventEmitter, Injector, Input, OnDestroy, OnInit, Output, inject } from '@angular/core'
import { FieldTree } from '@angular/forms/signals'
import { MatSelectChange } from '@angular/material/select'

import { AsiakkaanTuote, LaskunAlv, LaskutettavatPalvelut } from '@lemon/core-firebase/model/lasku'
import { Tuotenimike, tuotenimikkeet } from '@lemon/core-firebase/model/tuotenimikkeet'
import { TuettuKieli } from '@lemon/core/model/common'

import { Subject } from 'rxjs'
import { auditTime, takeUntil } from 'rxjs/operators'
import { arvonMuutokset, asetaArvo } from '../_jaettu-angular/_lomakkeet/arvon-muutokset'
import { LaskunRiviLomake } from './lasku-rivit.component'

export interface FieldError {
  field: string
  error: string
}

interface TuotenimikeKielella extends Tuotenimike {
  naytettavaNimi: string
  naytettavaKategoria: string
}

@Component({
  selector: 'app-lasku-rivi',
  templateUrl: './lasku-rivi.component.html',
  styleUrls: ['./lasku-rivi.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class LaskuRiviComponent implements OnInit, OnDestroy {
  // @ViewChild(TuoteHakuComponent, { static: true }) hakuKomponentti: TuoteHakuComponent
  // @ViewChildren(MatInput) numericKomponentit: QueryList<MatInput>
  // @ViewChildren(MatInput) currencyKomponentit: QueryList<MatInput>

  @Output() riviPoistettuTapahtuma: EventEmitter<FieldTree<LaskunRiviLomake, number>> = new EventEmitter()
  @Output() validaatioMuuttuiTapahtuma: EventEmitter<FieldError[]> = new EventEmitter()

  @Input() alvt: LaskunAlv[] = null
  @Input() parent: FieldTree<LaskunRiviLomake, number>
  @Input() paivitaValidaatiot: any
  @Input() laskunKieli: TuettuKieli = 'fi'

  private ngUnsubscribe = new Subject<void>()
  private readonly _injector = inject(Injector)

  private readonly EI_KERTALASKUTETTAVIA = [LaskutettavatPalvelut.JATKUVA_KUUKAUSILASKUTUS, LaskutettavatPalvelut.MAKSUT, LaskutettavatPalvelut.SAHKOISTEN_LASKUJEN_VASTAANOTTO]

  readonly tuotenimikkeet = tuotenimikkeet
  tuotenimikkeetRyhmat: { kategoria: string; tuotteet: TuotenimikeKielella[] }[] = []

  constructor() {}

  ngOnInit() {
    this.tuotenimikkeetRyhmat = this._naytaTuotteetKategorioittain()

    arvonMuutokset(this.parent, { injector: this._injector })
      .pipe(auditTime(100), takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        this.paivitaVirheet()
      })
  }

  focus() {
    // this.hakuKomponentti.focus()
  }

  poista() {
    this.riviPoistettuTapahtuma.next(this.parent)
  }

  markAsTouched() {
    // if (this.currencyKomponentit) {
    //   for (const komp of this.currencyKomponentit.toArray()) {
    //     komp.markAsTouched()
    //   }
    // }
    // if (this.numericKomponentit) {
    //   for (const komp of this.numericKomponentit.toArray()) {
    //     komp.value
    //   }
    // }
    // if (this.hakuKomponentti) {
    //   this.hakuKomponentti.markAsTouched()
    // }
  }

  compareAlvt(o1: LaskunAlv, o2: LaskunAlv): boolean {
    return o1?.tunniste === o2?.tunniste
  }

  compareTuotenimikkeet = (nimike: Tuotenimike | null, tuote: AsiakkaanTuote | null): boolean => {
    return nimike?.avain === tuote?.$key
  }

  async tuoteValittu(event: MatSelectChange) {
    const valittuNimike = event.value as Tuotenimike
    if (!valittuNimike) {
      return
    }

    const tuoteNimi = this.laskunKieli === 'en' ? valittuNimike.engNimi : valittuNimike.nimi

    const lisatiedot = this.parent.lisatiedot().value() || ''
    const tuote: AsiakkaanTuote = {
      $key: valittuNimike.avain,
      nimi: tuoteNimi + (lisatiedot?.trim() ? ': ' + lisatiedot.trim() : ''),
      hinta: valittuNimike.hinta || null,
      date: null,
      alv: null,
    }

    asetaArvo(this.parent.tuote, tuote)
    asetaArvo(this.parent.ahinta, valittuNimike.hinta ?? null)
    asetaArvo(this.parent.maara, valittuNimike.maara ?? 1)
  }

  paivitaVirheet() {
    this.validaatioMuuttuiTapahtuma.next(this.annaVirheet())
  }

  private annaVirheet(): FieldError[] {
    const errors: FieldError[] = []
    this.lisaaVirheet(errors, 'tuote')
    this.lisaaVirheet(errors, 'maara')
    this.lisaaVirheet(errors, 'ahinta')
    this.lisaaVirheet(errors, 'alv')
    this.lisaaVirheet(errors, 'ale')
    this.lisaaVirheet(errors, 'yhteensa')
    return errors
  }

  private lisaaVirheet(errors: FieldError[], identifier: keyof LaskunRiviLomake) {
    const field = this.parent[identifier]()
    if (field.errors().length > 0 && (field.touched() || field.dirty())) {
      for (const error of field.errors()) {
        errors.push({
          field: identifier,
          error: error.kind,
        })
      }
    }
  }
  ngOnDestroy() {
    this.ngUnsubscribe.next()
    this.ngUnsubscribe.complete()
  }

  private _naytaTuotteetKategorioittain(): { kategoria: string; tuotteet: TuotenimikeKielella[] }[] {
    const kieli = this.laskunKieli
    const naytettavatTuotteet = tuotenimikkeet.filter(t => !this.EI_KERTALASKUTETTAVIA.includes(t.avain))
    let tuotteetKielella: TuotenimikeKielella[] = naytettavatTuotteet.map(nimike => ({
      ...nimike,
      naytettavaNimi: kieli === 'en' ? nimike.engNimi : nimike.nimi,
      naytettavaKategoria: kieli === 'en' ? nimike.engKategoria : nimike.kategoria,
    }))

    tuotteetKielella = this._kasitteleTuntematonTuote(tuotteetKielella)

    const ryhmat = new Map<string, TuotenimikeKielella[]>()
    for (const tuote of tuotteetKielella) {
      const kategoria = tuote.naytettavaKategoria
      if (!ryhmat.has(kategoria)) {
        ryhmat.set(kategoria, [])
      }
      const kategorianTuotteet = ryhmat.get(kategoria)
      if (kategorianTuotteet) {
        kategorianTuotteet.push(tuote)
      }
    }

    return Array.from(ryhmat.entries()).map(([kategoria, tuotteet]) => ({
      kategoria,
      tuotteet,
    }))
  }

  // Jos formissa on tuote, jota ei löydy nykyisestä valikoimasta, näytetään se dropdownissa ainoana vaihtoehtona
  // Tapahtuu esimerkiksi silloin, jos lasku on aiemmin luotu tuntemattomalla tuotteella ja se täytyy hyvittää
  private _kasitteleTuntematonTuote(tuotteetKielella: TuotenimikeKielella[]): TuotenimikeKielella[] {
    const nykyinenTuote = this.parent.tuote().value() as AsiakkaanTuote | null
    if (nykyinenTuote?.$key) {
      const onTunnettuTuote = tuotteetKielella.some(t => t.avain === nykyinenTuote.$key)

      if (!onTunnettuTuote) {
        const vanhatuote: TuotenimikeKielella = {
          avain: nykyinenTuote.$key as LaskutettavatPalvelut,
          nimi: nykyinenTuote.nimi,
          engNimi: nykyinenTuote.nimi,
          hinta: nykyinenTuote.hinta || 0,
          maara: 1,
          kategoria: null,
          engKategoria: null,
          naytettavaNimi: nykyinenTuote.nimi,
          naytettavaKategoria: null,
          tilastoJarjestys: 999,
          tilastoKategoria: null,
        }
        asetaArvo(this.parent.lisatiedotPoissa, true)
        return [vanhatuote]
      }
    }
    return tuotteetKielella
  }
}
